import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Injector, Input, OnInit, Output, ViewChild, effect, runInInjectionContext, signal } from '@angular/core';
import { GoogleMapsModule, MapAdvancedMarker, MapInfoWindow } from '@angular/google-maps';
import { RouterModule } from '@angular/router';
import { SharedModule } from '@shared/shared.module';
import { BranchService } from './branch.service';
import { Subject } from 'rxjs/internal/Subject';
import { Branch } from './branch';
import { Store } from '@ngxs/store';
import { environment } from 'src/environments/environment.development';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { CONVERT_GEOLOCATION, SET_SELECTED_LOCATION_DETAILS } from '../geolocation/store/geolocation.action';
import { DefaultAddressForConversionPayload, IGeocodingConversionPayload } from '../geolocation/store/geolocation.interface';
import { CustomerPartnerDetailsComponent } from '../customer-partner/customer-partner-details/customer-partner-details.component';
import { UPDATE_GEO_INDICATOR } from '@store/sat-motor/sat-motor.action';

declare global {
  interface Window {
    initMap: () => void;
  }
}

@Component({
  selector: 'app-branches-map',
  standalone: true,
  imports: [
    CommonModule,
    GoogleMapsModule,
    RouterModule,
    SharedModule,
  ],
  providers: [BranchService],
  templateUrl: './branches-map.component.html',
  styleUrl: './branches-map.component.scss'
})
export class BranchesMapComponent  implements OnInit {
  @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow | undefined;
  // @ViewChild(CustomerPartnerDetailsComponent) geoInd!: CustomerPartnerDetailsComponent;
  @ViewChild("search", { static: false })
  searchInput!: ElementRef<HTMLInputElement>;

  @Output() formPopulated = new EventEmitter<any>();
  @Input() mapVisible: boolean = false;
  @Input()
  get autoCompleteSearchOptions(): Partial<google.maps.places.AutocompleteRequest> {
    if (!this._autoCompleteSearchOptions) {
      this._autoCompleteSearchOptions = {
        includedRegionCodes: ['my'],
      };
    }
    return this._autoCompleteSearchOptions;
  }

  set autoCompleteSearchOptions(
    options: Partial<google.maps.places.AutocompleteRequest>
  ) {
    this._autoCompleteSearchOptions = {
      ...this.autoCompleteSearchOptions,
      ...options,
    };
  }

  private _autoCompleteSearchOptions: Partial<google.maps.places.AutocompleteRequest>;
  private searchQuerySubject: Subject<string> = new Subject<string>();
  private destroy$: Subject<void> = new Subject<void>();
  autocompleteSuggestions: Array<google.maps.places.AutocompleteSuggestion>;
  branches = signal<Branch[]>([]);
  center: google.maps.LatLngLiteral = { lat: 3.1390, lng: 101.6869 }; // Center of Kuala Lumpur, Malaysia
  zoom = 12; // Adjust zoom level as needed
  markers: BranchMapMarker[] = [];
  selectedBranch = signal<Branch | null>(null);
  googleMapsLoaded = signal<boolean>(false);
  disabled: boolean = false;
  geocodedInd: boolean;
  isStreetView: boolean = false;
  
  showSuggestions: boolean = false;
  matchedSubstrings: number;
  searchAddress = '';
  searchMode: 'address' | 'latLng' = 'address';
  latitude: string = '';
  longitude: string = '';
  geoMessageErr: String;
  isGeoMessageErr: boolean = false;
  COUNTRY_MALAYSIA = 'Malaysia';
  geoPostCodeErr = 'Address is incomplete, please re-select with higher accuracy.';
  geoCountryErr = 'Countries other than Malaysia. Not allowed to proceed.';
  mapOptions: google.maps.MapOptions = {
    fullscreenControl: false,
    mapId: environment.googleMapId
  };
  

  constructor(
    private branchService: BranchService, 
    @Inject(Injector) private injector: Injector,
	private cdr: ChangeDetectorRef,
    private store: Store) {
    runInInjectionContext(this.injector, () => {
      effect(() => {
        this.markers = this.getMarkers();
      }, { allowSignalWrites: true });
    });
  }

  ngOnInit() {
    this.loadGoogleMapsScript();
  }

  loadGoogleMapsScript() {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleApiKey}&libraries=places,marker&callback=initMap&v=beta&loading=async`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  ngAfterViewInit() {
    window.initMap = this.initMap.bind(this);
    this.watchSearchQueryChange();

    if (this.searchInput) {
      this.prefillSearchAddress();
    }
  }
  
  onMapInitialized(map: google.maps.Map) {
    const streetView = map.getStreetView();
    google.maps.event.addListener(streetView, 'visible_changed', () => {
      this.isStreetView = streetView.getVisible();
	  this.cdr.detectChanges();
    });
  }

  onSearchModeChange(mode) {
    if (this.markers.length > 0) {
      const marker = this.markers[0];
      const lat = marker.position.lat;
      const lng = marker.position.lng;

      if (mode === 'latLng') {
        this.showSuggestions = false;
      } else if (mode === 'address') {
        this.showSuggestions = true;
        this.searchQuerySubject.next(this.searchAddress);
        this.showSuggestedAddress(this.searchAddress);
      }
    } else {
      this.searchAddress = '';
      this.latitude = '';
      this.longitude = '';
    }
    
  }

  watchSearchQueryChange() {
    this.searchQuerySubject
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((searchQuery) => {
        this.autocompleteSuggestions = [];
        if (searchQuery) {
          this.showSuggestions = true;
          this.showSuggestedAddress(searchQuery);
        } else {
          this.showSuggestions = false;
        }
      });
  }

  async showSuggestedAddress(searchQuery: string) {
    const { AutocompleteSuggestion } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;
    const request: google.maps.places.AutocompleteRequest = {
          input: searchQuery,
          includedRegionCodes: this.autoCompleteSearchOptions.includedRegionCodes,
          language: 'en',
          region: 'my',
    };
    try {
    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    this.autocompleteSuggestions = suggestions;
    // Access suggestion data via: suggestion.placePrediction.text.text
    //                             suggestion.placePrediction.placeId
    //                             suggestion.placePrediction.text.matches[0]?.length
  } catch (e) {
    console.error('Autocomplete fetch failed:', e);
    this.autocompleteSuggestions = [];
  }
  }

  async prefillSearchAddress() {
    google.maps.event.trigger(this.searchInput, "focus");
    google.maps.event.trigger(this.searchInput, "keydown", {
      keyCode: 13,
    });
   const { Place } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;

   try {
      const { places } = await Place.searchByText({
        textQuery: this.searchAddress,
        fields: ["formattedAddress", "location", "displayName", "id", "plusCode"],
      });
      if (places.length > 0) {
        this.setMarkerFromGeometry(places[0]);
      }
   } catch (e) {
      console.error('Place search failed:', e);
   }

    
  }

  setMarkerFromGeometry(result: google.maps.places.Place) {
    if (result.location) {
      this.markers = [];

      const marker: BranchMapMarker = {
        position: { lat: result.location.lat(), lng: result.location.lng() },
        title: result.displayName || 'New Marker',
        branch: null,
      };
      this.markers.push(marker);
      this.markers = [...this.markers];
    }
  }

  searchByLatLng() {
    const lat = parseFloat(this.latitude);
    const lng = parseFloat(this.longitude);
    if (!isNaN(lat) && !isNaN(lng)) {
      const mapMouseEvent: google.maps.MapMouseEvent = {
        latLng: new google.maps.LatLng(lat, lng),
        domEvent: undefined,
        stop: () => {}
      };
  
      this.zoom = 12;
      this.addMarker(mapMouseEvent, null);
    } else {
      console.log('Please enter valid Latitude and Longitude');
    }
  }
  
  
  async getPlaceDetails(placeId: string, setMarker: boolean) {
    const { Place } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;
    const place = new Place({ id: placeId });
    try {
      await place.fetchFields({
        fields: ["formattedAddress", "location", "displayName", "addressComponents", "id", "plusCode"]
      });
      if (setMarker) this.setMarkerFromGeometry(place);
      this.internalGeocodingConversion(place);
    } catch (e) {
      console.error('Error fetching place details:', e);
    }
  }

  internalGeocodingConversion(address: google.maps.places.Place | google.maps.places.PlaceResult) {
    this.isGeoMessageErr = false;
    let addressComponents: google.maps.places.AddressComponent[] | google.maps.GeocoderAddressComponent[] | undefined;
    let formattedAddress: string | null | undefined;
    let displayName: string | null | undefined;
    let location: google.maps.LatLng | null | undefined;
    let plusCode: string | null | undefined;
    let placeId: string | undefined;

    if ("id" in address) {
      addressComponents = address.addressComponents;
      formattedAddress = address.formattedAddress;
      displayName = address.displayName;
      location = address.location;
      plusCode = address.plusCode?.globalCode;
      placeId = address.id;
    } else {
      addressComponents = address.address_components;
      formattedAddress = address.formatted_address;
      displayName = address.name;
      location = address.geometry?.location;
      plusCode = address.plus_code?.global_code;
      placeId = address.place_id;
    }

    let payload = this.formatAddressComponentForPayload(addressComponents);
    payload = {
    ...payload,
    formatted_address: formattedAddress ?? "",
    name: displayName ?? "",
    clientGeoPlusCode: plusCode?.toString() ?? "",
    clientGeoLatitude: location?.lat().toString() ?? "",
    clientGeoLongitude: location?.lng().toString() ?? "",
  };
    console.warn('geo address :', address, "formatted address :", payload);
    if(payload.postal_code && this.COUNTRY_MALAYSIA === payload.country){
      this.store.dispatch(new CONVERT_GEOLOCATION(payload)).subscribe(() => {
        const geolocation = this.store.selectSnapshot((state)=> state.GeolocationState.selectedLocation.convertedResponse)
        const setLocationPayload = {
          ...geolocation,
          clientGeoPlaceId: placeId,
          clientGeoFormattedAddress: formattedAddress,
          // geocodedInd:true
        };
        this.formPopulated.emit(setLocationPayload);
        this.store.dispatch(
          new SET_SELECTED_LOCATION_DETAILS(setLocationPayload)
        );
      });
    }else{
      const data = this.extractAddressData(addressComponents);
      const setLocationPayload = {
        clientUnitNo: data.unitNo?.trim(),
        clientAddress1: data.address1?.trim(),
        clientAddress2: data.address2?.trim(),
        clientAddress3: data.address3?.trim(),
        clientPostCode: data.postCode,
        clientCityDesc: data.city,
        clientStateDesc: data.state,
        clientCountryDesc: data.country
      };
      this.formPopulated.emit(setLocationPayload);
    }
  }
  

  formatAddressComponentForPayload(address: google.maps.places.AddressComponent[] | google.maps.GeocoderAddressComponent[])
  : IGeocodingConversionPayload {
    let formatted: IGeocodingConversionPayload =
      new DefaultAddressForConversionPayload();
    address.forEach((item) => {
      const longText = "longText" in item ? item.longText : item.long_name;
      if (item.types.includes("administrative_area_level_1")) {
        formatted.administrative_area_level_1 = longText;
      }
      if (item.types.includes("locality")) {
        formatted.locality = longText;
      }
      if (item.types.includes("country")) {
        formatted.country = longText;
      }
      if (item.types.includes("postal_code")) {
        formatted.postal_code = longText;
      }
      return item;
    });

    return formatted;
  }
  

  onInputChange(event) {
    const searchQuery = event.target.value;
    this.searchQuerySubject.next(searchQuery);
  }

  onInputFocus() {
    this.showSuggestions = true;
  }

  onInputBlur() {
    setTimeout(() => {
      this.showSuggestions = false;
    }, 1000);
  }

  onSuggestionClick(selectedSuggestion: google.maps.places.AutocompleteSuggestion) {
     this.showSuggestions = false;
     this.searchAddress = selectedSuggestion.placePrediction.text.text;
     this.getPlaceDetails(selectedSuggestion.placePrediction.placeId, true);
  }

  initMap() {
    this.branches.set(this.branchService.getBranches());
    this.googleMapsLoaded.set(true);
    
    runInInjectionContext(this.injector, () => {
      effect(() => {
        this.markers = this.getMarkers();
      }, { allowSignalWrites: true });
    });
  }

  getMarkers() {
    return this.branches()
      .map((branch) => {
        const marker: BranchMapMarker = {
          position: { lat: branch.lat, lng: branch.lng },
          title: branch.name,
          branch: branch,
        };
        return marker;
      })
      .filter(
        (marker) => !isNaN(marker.position.lat) && !isNaN(marker.position.lng)
      );
  }

  addMarker(event: google.maps.MapMouseEvent, result: google.maps.places.PlaceResult) {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
  
    if (this.searchMode === 'latLng') {
      this.latitude = lat.toString();
      this.longitude = lng.toString();
      this.getAddress(lat, lng, result);
    } else if (this.searchMode === 'address') {
      this.getAddress(lat, lng, result);
    }
    console.log(`Latitude: ${lat}, Longitude: ${lng}`);
  
    this.markers = [];

    const marker: BranchMapMarker = {
      position: { lat, lng },
      title: 'New Marker',
      branch: null,
    };
    this.markers.push(marker);
  
    this.markers = [...this.markers];
  
    this.center = { lat, lng };
    this.store.dispatch(new UPDATE_GEO_INDICATOR(true));
  }
  

  getAddress(lat: number, lng: number, result: google.maps.places.PlaceResult) {
    const geocoder = new google.maps.Geocoder();
    const latlng = { lat: lat, lng: lng };

    if (result && result?.formatted_address) {
      this.searchAddress = result.formatted_address;
      this.internalGeocodingConversion(result as google.maps.places.PlaceResult);
    } else {
      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          if (results[0]) {
            this.searchAddress = results[0].formatted_address;
            const addressComponents = results[0].address_components;
            
            this.internalGeocodingConversion(results[0] as google.maps.places.PlaceResult);
          } else {
            console.error('No results found');
          }
        } else {
          console.error('Geocoder failed due to: ' + status);
        }
      });
    }
  }
  
  extractAddressData(addressComponents: any) {
    let data: any = {
      unitNo: '',
      address1: '',
      address2: '',
      address3: '',
      postCode: '',
      city: '',
      state: '',
      country: ''
    };
  
    addressComponents.forEach(component => {
      const types = component.types;
      const longText = "longText" in component ? component.longText : component.long_name;
      if (types.includes('street_number')) {
        data.unitNo = longText;
      }
      if (types.includes('route') || types.includes('plus_code')) {
        data.address1 = longText;
      }
      if (types.includes('sublocality_level_1') || types.includes('locality')) {
        data.city = longText;
      }
      if (types.includes('administrative_area_level_1')) {
        data.state = longText;
      }
      if (types.includes('postal_code')) {
        data.postCode = longText;
      }
      if (types.includes('country')) {
        data.country = longText;
      }

    });
  
    return data;
  }  
  
  searchLocation() {
    if (!this.searchAddress) {
      return;
    }
  
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: this.searchAddress }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results[0]) {
        const location = results[0].geometry.location;
        this.center = { lat: location.lat(), lng: location.lng() };
        this.zoom = 15;
  
        const mapMouseEvent: google.maps.MapMouseEvent = {
          latLng: location,
          domEvent: undefined,
          stop: () => {}
        };
  
        this.addMarker(mapMouseEvent, null);
      } else {
        console.log('Geocode was not successful for the following reason: ' + status);
      }
    });
  }

  openInfoWindow(branch: Branch, marker: MapAdvancedMarker): void {
    this.selectedBranch.set(branch);
    if (this.infoWindow) {
      this.infoWindow.openAdvancedMarkerElement(marker.advancedMarker);
    }
  }
  clearAddress(){
    this.searchAddress ='';
    this.center = { lat: 3.1390, lng: 101.6869 }; 
    this.zoom = 12; 
    this.markers = []; 
    this.geocodedInd = false;

  }
}

export interface BranchMapMarker {
  branch: Branch | null;
  position: {
    lat: number;
    lng: number;
  };
  title: string;
  click?: () => void;
}
